import React from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

/**
 * Animation variants for different reveal effects
 */
const animations = {
  fadeUp: {
    hidden: 'opacity-0 translate-y-12',
    visible: 'opacity-100 translate-y-0',
  },
  fadeDown: {
    hidden: 'opacity-0 -translate-y-12',
    visible: 'opacity-100 translate-y-0',
  },
  fadeLeft: {
    hidden: 'opacity-0 translate-x-12',
    visible: 'opacity-100 translate-x-0',
  },
  fadeRight: {
    hidden: 'opacity-0 -translate-x-12',
    visible: 'opacity-100 translate-x-0',
  },
  fadeIn: {
    hidden: 'opacity-0',
    visible: 'opacity-100',
  },
  scaleUp: {
    hidden: 'opacity-0 scale-95',
    visible: 'opacity-100 scale-100',
  },
  scaleIn: {
    hidden: 'opacity-0 scale-90',
    visible: 'opacity-100 scale-100',
  },
  slideUp: {
    hidden: 'opacity-0 translate-y-20',
    visible: 'opacity-100 translate-y-0',
  },
  rotateIn: {
    hidden: 'opacity-0 rotate-6 scale-95',
    visible: 'opacity-100 rotate-0 scale-100',
  },
  blurIn: {
    hidden: 'opacity-0 blur-sm',
    visible: 'opacity-100 blur-0',
  },
};

/**
 * Duration classes
 */
const durations = {
  fast: 'duration-300',
  normal: 'duration-500',
  slow: 'duration-700',
  slower: 'duration-1000',
};

/**
 * Easing classes
 */
const easings = {
  linear: 'ease-linear',
  ease: 'ease',
  easeIn: 'ease-in',
  easeOut: 'ease-out',
  easeInOut: 'ease-in-out',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
};

/**
 * ScrollReveal Component
 * Wraps children with scroll-triggered animations
 */
const ScrollReveal = ({
  children,
  animation = 'fadeUp',
  duration = 'slow',
  easing = 'easeOut',
  delay = 0,
  threshold = 0.1,
  triggerOnce = true,
  className = '',
  as: Component = 'div',
  ...props
}) => {
  const { ref, isVisible } = useScrollAnimation({
    threshold,
    triggerOnce,
  });

  const animationClasses = animations[animation] || animations.fadeUp;
  const durationClass = durations[duration] || durations.normal;

  return (
    <Component
      ref={ref}
      className={`
        transition-all ${durationClass} ${easings[easing] || easings.easeOut}
        ${isVisible ? animationClasses.visible : animationClasses.hidden}
        ${className}
      `}
      style={{ transitionDelay: `${delay}ms` }}
      {...props}
    >
      {children}
    </Component>
  );
};

/**
 * StaggerContainer Component
 * Animates children with staggered delays
 */
const StaggerContainer = ({
  children,
  animation = 'fadeUp',
  duration = 'slow',
  staggerDelay = 100,
  threshold = 0.1,
  className = '',
  ...props
}) => {
  const { ref, isVisible } = useScrollAnimation({
    threshold,
    triggerOnce: true,
  });

  const animationClasses = animations[animation] || animations.fadeUp;
  const durationClass = durations[duration] || durations.normal;

  return (
    <div ref={ref} className={className} {...props}>
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;
        
        return (
          <div
            className={`
              transition-all ${durationClass} ease-out
              ${isVisible ? animationClasses.visible : animationClasses.hidden}
            `}
            style={{ transitionDelay: `${index * staggerDelay}ms` }}
          >
            {child}
          </div>
        );
      })}
    </div>
  );
};

/**
 * TextReveal Component
 * Reveals text word by word or letter by letter
 */
const TextReveal = ({
  children,
  type = 'words', // 'words' or 'letters'
  staggerDelay = 50,
  duration = 'normal',
  className = '',
  ...props
}) => {
  const { ref, isVisible } = useScrollAnimation({
    threshold: 0.5,
    triggerOnce: true,
  });

  if (typeof children !== 'string') {
    return <span className={className}>{children}</span>;
  }

  const items = type === 'words' ? children.split(' ') : children.split('');
  const separator = type === 'words' ? '\u00A0' : '';
  const durationClass = durations[duration] || durations.normal;

  return (
    <span ref={ref} className={`inline-block ${className}`} {...props}>
      {items.map((item, index) => (
        <span
          key={index}
          className={`
            inline-block transition-all ${durationClass} ease-out
            ${isVisible 
              ? 'opacity-100 translate-y-0 blur-0' 
              : 'opacity-0 translate-y-4 blur-[2px]'
            }
          `}
          style={{ transitionDelay: `${index * staggerDelay}ms` }}
        >
          {item}{separator}
        </span>
      ))}
    </span>
  );
};

/**
 * ParallaxSection Component
 * Creates parallax scrolling effect
 */
const ParallaxSection = ({
  children,
  speed = 0.3,
  className = '',
  ...props
}) => {
  const [offset, setOffset] = React.useState(0);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const scrolled = (window.innerHeight - rect.top) / window.innerHeight;
      setOffset(scrolled * speed * 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`} {...props}>
      <div
        style={{ transform: `translateY(${offset}px)` }}
        className="transition-transform duration-75 ease-linear"
      >
        {children}
      </div>
    </div>
  );
};

/**
 * CountUp Component
 * Animated number counter
 */
const CountUp = ({
  end,
  duration = 2000,
  prefix = '',
  suffix = '',
  className = '',
}) => {
  const [count, setCount] = React.useState(0);
  const { ref, isVisible } = useScrollAnimation({
    threshold: 0.5,
    triggerOnce: true,
  });

  React.useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const startValue = 0;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (easeOutExpo)
      const easeProgress = 1 - Math.pow(2, -10 * progress);
      const currentCount = Math.floor(startValue + (end - startValue) * easeProgress);
      
      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}{count}{suffix}
    </span>
  );
};

/**
 * ScrollProgress Component
 * Progress bar showing scroll position
 */
const ScrollProgress = ({ className = '' }) => {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setProgress(Math.min(100, Math.max(0, scrollPercent)));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className={`fixed top-0 left-0 right-0 h-[2px] bg-transparent z-[100] ${className}`}>
      <div 
        className="h-full bg-gradient-to-r from-accent via-accent to-violet-500 transition-all duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

export {
  ScrollReveal,
  StaggerContainer,
  TextReveal,
  ParallaxSection,
  CountUp,
  ScrollProgress,
  animations,
  durations,
  easings,
};
